import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Database,
  Plus,
  Pencil,
  Trash2,
  Save,
  RefreshCw,
  Settings,
  Package,
  Tag,
  FileText,
  Hash,
  Calendar,
  Link,
  Image,
  List,
  CheckSquare,
  Type,
  Search,
  Upload,
  Download,
  Zap
} from "lucide-react";

const API = "/api";

const METAFIELD_TYPES = [
  { value: "single_line_text_field", label: "Tek Satır Metin", icon: Type },
  { value: "multi_line_text_field", label: "Çok Satırlı Metin", icon: FileText },
  { value: "number_integer", label: "Tam Sayı", icon: Hash },
  { value: "number_decimal", label: "Ondalık Sayı", icon: Hash },
  { value: "boolean", label: "Evet/Hayır", icon: CheckSquare },
  { value: "date", label: "Tarih", icon: Calendar },
  { value: "date_time", label: "Tarih ve Saat", icon: Calendar },
  { value: "url", label: "URL", icon: Link },
  { value: "json", label: "JSON", icon: Database },
  { value: "list.single_line_text_field", label: "Metin Listesi", icon: List },
  { value: "file_reference", label: "Dosya Referansı", icon: Image },
  { value: "product_reference", label: "Ürün Referansı", icon: Package },
];

const OWNER_TYPES = [
  { value: "PRODUCT", label: "Ürünler", icon: Package },
  { value: "VARIANT", label: "Varyantlar", icon: Tag },
  { value: "COLLECTION", label: "Koleksiyonlar", icon: List },
  { value: "CUSTOMER", label: "Müşteriler", icon: FileText },
  { value: "ORDER", label: "Siparişler", icon: FileText },
];

export default function MetafieldsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Metafield Definitions
  const [definitions, setDefinitions] = useState([]);
  const [filteredDefinitions, setFilteredDefinitions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOwnerType, setFilterOwnerType] = useState("all");
  
  // Sync Mappings
  const [mappings, setMappings] = useState([]);
  const [nebimFields, setNebimFields] = useState([]);
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState(null);
  
  // Form
  const [definitionForm, setDefinitionForm] = useState({
    name: "",
    namespace: "custom",
    key: "",
    description: "",
    type: "single_line_text_field",
    ownerType: "PRODUCT",
    validations: [],
    pin: true
  });
  
  const [mappingForm, setMappingForm] = useState({
    nebimField: "",
    shopifyNamespace: "custom",
    shopifyKey: "",
    transform: "direct", // direct, uppercase, lowercase, custom
    customTransform: ""
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    let filtered = definitions;
    
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.namespace?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterOwnerType !== "all") {
      filtered = filtered.filter(d => d.ownerType === filterOwnerType);
    }
    
    setFilteredDefinitions(filtered);
  }, [searchTerm, filterOwnerType, definitions]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [definitionsRes, mappingsRes, nebimFieldsRes] = await Promise.all([
        axios.get(`${API}/metafields/definitions?token=${token}`),
        axios.get(`${API}/metafields/mappings?token=${token}`),
        axios.get(`${API}/metafields/nebim-fields?token=${token}`)
      ]);
      
      setDefinitions(definitionsRes.data.definitions || []);
      setMappings(mappingsRes.data.mappings || []);
      setNebimFields(nebimFieldsRes.data.fields || []);
    } catch (error) {
      console.error("Metafields fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingDefinition(null);
    setDefinitionForm({
      name: "",
      namespace: "custom",
      key: "",
      description: "",
      type: "single_line_text_field",
      ownerType: "PRODUCT",
      validations: [],
      pin: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (definition) => {
    setEditingDefinition(definition);
    setDefinitionForm({
      name: definition.name || "",
      namespace: definition.namespace || "custom",
      key: definition.key || "",
      description: definition.description || "",
      type: definition.type || "single_line_text_field",
      ownerType: definition.ownerType || "PRODUCT",
      validations: definition.validations || [],
      pin: definition.pin !== false
    });
    setDialogOpen(true);
  };

  const saveDefinition = async () => {
    if (!definitionForm.name || !definitionForm.key) {
      toast.error("Ad ve Key zorunludur");
      return;
    }

    setSaving(true);
    try {
      if (editingDefinition) {
        await axios.put(`${API}/metafields/definitions/${editingDefinition.id}?token=${token}`, definitionForm);
        toast.success("Metafield tanımı güncellendi");
      } else {
        await axios.post(`${API}/metafields/definitions?token=${token}`, definitionForm);
        toast.success("Metafield tanımı oluşturuldu");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteDefinition = async (definitionId) => {
    if (!confirm("Bu metafield tanımını silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/metafields/definitions/${definitionId}?token=${token}`);
      toast.success("Metafield tanımı silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const saveMapping = async () => {
    if (!mappingForm.nebimField || !mappingForm.shopifyKey) {
      toast.error("Nebim alanı ve Shopify key zorunludur");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/metafields/mappings?token=${token}`, mappingForm);
      toast.success("Eşleştirme kaydedildi");
      setMappingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteMapping = async (mappingId) => {
    try {
      await axios.delete(`${API}/metafields/mappings/${mappingId}?token=${token}`);
      toast.success("Eşleştirme silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const syncMetafields = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/metafields/sync?token=${token}`);
      toast.success(`${response.data.synced || 0} ürünün metafield'ları güncellendi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Senkronizasyon başarısız");
    } finally {
      setSyncing(false);
    }
  };

  const deployToShopify = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/metafields/deploy?token=${token}`);
      toast.success("Metafield tanımları Shopify'a aktarıldı");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Aktarım başarısız");
    } finally {
      setSyncing(false);
    }
  };

  const getTypeInfo = (type) => {
    return METAFIELD_TYPES.find(t => t.value === type) || { label: type, icon: Database };
  };

  const getOwnerInfo = (ownerType) => {
    return OWNER_TYPES.find(o => o.value === ownerType) || { label: ownerType, icon: Package };
  };

  return (
    <div className="space-y-6 fade-in" data-testid="metafields-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Metafield Yönetimi</h1>
          <p className="text-slate-500 mt-1">Shopify metafield tanımları ve Nebim eşleştirmeleri</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={syncMetafields} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            Senkronize Et
          </Button>
          <Button onClick={openAddDialog} className="bg-violet-600 hover:bg-violet-700">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Tanım
          </Button>
        </div>
      </div>

      <Tabs defaultValue="definitions" className="space-y-6">
        <TabsList>
          <TabsTrigger value="definitions" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Tanımlar
          </TabsTrigger>
          <TabsTrigger value="mappings" className="flex items-center gap-2">
            <Link className="w-4 h-4" />
            Eşleştirmeler
          </TabsTrigger>
        </TabsList>

        {/* Definitions Tab */}
        <TabsContent value="definitions" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <Database className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{definitions.length}</p>
                    <p className="text-xs text-slate-500">Toplam Tanım</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{definitions.filter(d => d.ownerType === "PRODUCT").length}</p>
                    <p className="text-xs text-slate-500">Ürün Metafield</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Link className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{mappings.length}</p>
                    <p className="text-xs text-slate-500">Eşleştirme</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Zap className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{definitions.filter(d => d.deployedToShopify).length}</p>
                    <p className="text-xs text-slate-500">Shopify'da</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Ad, key veya namespace ile ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterOwnerType} onValueChange={setFilterOwnerType}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Tüm Türler" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Türler</SelectItem>
                    {OWNER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={deployToShopify} disabled={syncing}>
                  <Upload className="w-4 h-4 mr-2" />
                  Shopify'a Aktar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Definitions Table */}
          <Card>
            <CardHeader>
              <CardTitle>Metafield Tanımları</CardTitle>
              <CardDescription>{filteredDefinitions.length} tanım listeleniyor</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : filteredDefinitions.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henüz metafield tanımı oluşturulmadı</p>
                  <Button onClick={openAddDialog} className="mt-4" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    İlk Tanımı Oluştur
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ad</TableHead>
                      <TableHead>Namespace / Key</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Ait Olduğu</TableHead>
                      <TableHead className="text-center">Sabitlenmiş</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDefinitions.map((definition) => {
                      const typeInfo = getTypeInfo(definition.type);
                      const ownerInfo = getOwnerInfo(definition.ownerType);
                      const TypeIcon = typeInfo.icon;
                      const OwnerIcon = ownerInfo.icon;
                      
                      return (
                        <TableRow key={definition.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{definition.name}</p>
                              {definition.description && (
                                <p className="text-xs text-slate-500 truncate max-w-[200px]">{definition.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="text-sm bg-slate-100 px-2 py-0.5 rounded">
                              {definition.namespace}.{definition.key}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="flex items-center gap-1 w-fit">
                              <TypeIcon className="w-3 h-3" />
                              {typeInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1 w-fit">
                              <OwnerIcon className="w-3 h-3" />
                              {ownerInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {definition.pin ? (
                              <Badge className="bg-emerald-500">Evet</Badge>
                            ) : (
                              <Badge variant="outline">Hayır</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="icon" variant="ghost" onClick={() => openEditDialog(definition)}>
                                <Pencil className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => deleteDefinition(definition.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mappings Tab */}
        <TabsContent value="mappings" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Nebim - Shopify Eşleştirmeleri</CardTitle>
                  <CardDescription>Nebim alanlarını Shopify metafield'larına eşleyin</CardDescription>
                </div>
                <Button onClick={() => setMappingDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Eşleştirme
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {mappings.length === 0 ? (
                <div className="text-center py-12">
                  <Link className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henüz eşleştirme oluşturulmadı</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nebim Alanı</TableHead>
                      <TableHead>Shopify Metafield</TableHead>
                      <TableHead>Dönüşüm</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mappings.map((mapping) => (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <code className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded">
                            {mapping.nebimField}
                          </code>
                        </TableCell>
                        <TableCell>
                          <code className="bg-violet-50 text-violet-700 px-2 py-0.5 rounded">
                            {mapping.shopifyNamespace}.{mapping.shopifyKey}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{mapping.transform || "Direkt"}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={() => deleteMapping(mapping.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Definition Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDefinition ? "Tanımı Düzenle" : "Yeni Metafield Tanımı"}</DialogTitle>
            <DialogDescription>
              Shopify mağazanız için özel veri alanları oluşturun
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Ad *</Label>
              <Input
                id="name"
                value={definitionForm.name}
                onChange={(e) => setDefinitionForm({...definitionForm, name: e.target.value})}
                placeholder="Örn: Ürün Ağırlığı"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="namespace">Namespace</Label>
                <Input
                  id="namespace"
                  value={definitionForm.namespace}
                  onChange={(e) => setDefinitionForm({...definitionForm, namespace: e.target.value})}
                  placeholder="custom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="key">Key *</Label>
                <Input
                  id="key"
                  value={definitionForm.key}
                  onChange={(e) => setDefinitionForm({...definitionForm, key: e.target.value.toLowerCase().replace(/\s+/g, "_")})}
                  placeholder="urun_agirligi"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Veri Tipi</Label>
              <Select 
                value={definitionForm.type} 
                onValueChange={(v) => setDefinitionForm({...definitionForm, type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METAFIELD_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Ait Olduğu</Label>
              <Select 
                value={definitionForm.ownerType} 
                onValueChange={(v) => setDefinitionForm({...definitionForm, ownerType: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OWNER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                value={definitionForm.description}
                onChange={(e) => setDefinitionForm({...definitionForm, description: e.target.value})}
                placeholder="Bu metafield ne için kullanılıyor?"
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Sabitle</Label>
                <p className="text-xs text-slate-500">Admin panelinde üste sabitle</p>
              </div>
              <Switch
                checked={definitionForm.pin}
                onCheckedChange={(v) => setDefinitionForm({...definitionForm, pin: v})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={saveDefinition} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {editingDefinition ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mapping Dialog */}
      <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Eşleştirme</DialogTitle>
            <DialogDescription>
              Nebim alanını Shopify metafield'ına eşleyin
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nebim Alanı</Label>
              <Select 
                value={mappingForm.nebimField} 
                onValueChange={(v) => setMappingForm({...mappingForm, nebimField: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nebim alanı seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {nebimFields.map((field) => (
                    <SelectItem key={field.key} value={field.key}>
                      {field.label || field.key}
                    </SelectItem>
                  ))}
                  <SelectItem value="ItemDescription1">Ürün Açıklama 1</SelectItem>
                  <SelectItem value="ItemDescription2">Ürün Açıklama 2</SelectItem>
                  <SelectItem value="BrandCode">Marka Kodu</SelectItem>
                  <SelectItem value="ColorCode">Renk Kodu</SelectItem>
                  <SelectItem value="SizeCode">Beden</SelectItem>
                  <SelectItem value="Weight">Ağırlık</SelectItem>
                  <SelectItem value="CustomField1">Özel Alan 1</SelectItem>
                  <SelectItem value="CustomField2">Özel Alan 2</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Shopify Namespace</Label>
                <Input
                  value={mappingForm.shopifyNamespace}
                  onChange={(e) => setMappingForm({...mappingForm, shopifyNamespace: e.target.value})}
                  placeholder="custom"
                />
              </div>
              <div className="space-y-2">
                <Label>Shopify Key</Label>
                <Input
                  value={mappingForm.shopifyKey}
                  onChange={(e) => setMappingForm({...mappingForm, shopifyKey: e.target.value})}
                  placeholder="alan_adi"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Dönüşüm</Label>
              <Select 
                value={mappingForm.transform} 
                onValueChange={(v) => setMappingForm({...mappingForm, transform: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">Direkt (Değişiklik Yok)</SelectItem>
                  <SelectItem value="uppercase">BÜYÜK HARF</SelectItem>
                  <SelectItem value="lowercase">küçük harf</SelectItem>
                  <SelectItem value="trim">Boşlukları Temizle</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingDialogOpen(false)}>İptal</Button>
            <Button onClick={saveMapping} disabled={saving}>
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
